import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory} from 'react-router-dom';

import {dashboardTabId, minDate, UserType} from '../../../util/uiconstants';
import Utility from '../../../util/utilityfunctions';
import {labels} from '../../../util/utils';

import {getEngagementMetrics} from '../../../actions/Metrics/engagementMetricsActions';
import {getPermissions} from '../../../actions/Project/projectAction';

import OutputText from '@ey/canvascoreservices/OutputText';

import TemplateWithSidebar from '../../Templates/TemplateWithSidebar';
import TabNavigation from '../../CoreWrappers/TabNavigation';
import RightDashboardLoader from '../../Common/Loaders/RightDashboardLoader';
import Sidebar from '../../Common/Sidebar';
import LabelAndDate from '../../Common/LabelAndDate';
import StyledDashboard from './StyledDashboard';

import ProjectManagement from './ProjectManagement/ProjectManagement';
import AuditInsight from './AuditInsight/AuditInsight';
import GroupAudit from './GroupAudit';
import Milestones from './Milestones';
import Reporting from './Reporting/Reporting';
import CCP from './CCP';
import StyledDashboardGrid from './StyledDashboardGrid';

const Dashboard = () => {
	const [activeTab, setActiveTab] = useState(null);
	const [isLoading, setIsLoading] = useState(true);

	const history = useHistory();
	const dispatch = useDispatch();

	const engagementMetrics = useSelector(({engagementMetrics}) => engagementMetrics);
	const permissions = useSelector(({permissions}) => permissions?.permissions);
	const userPreferences = useSelector(({userPreferences}) => userPreferences);
	const [selectedEngagements, orgChart] = useSelector(({dashboardContext}) => [
		dashboardContext.engagements,
		dashboardContext.orgChart
	]);
	const projectId = Utility.getProjectIdFromUrl();
	const selectedMetrics = engagementMetrics?.[activeTab?.id]?.filter((eng) => selectedEngagements?.includes(eng.engId));
	const lastRefreshed = selectedMetrics?.find((x) => x.loadedOn !== minDate)?.loadedOn;

	const tabs = [
		{
			id: dashboardTabId.milestones,
			label: labels.milestones,
			hash: '#milestones',
			visible: !!permissions?.canAccessMilestone,
			View: Milestones
		},
		{
			id: dashboardTabId.projectManagement,
			label: labels.projectManagement,
			hash: '#projectManagement',
			visible: !!permissions?.canAccessProjectManagement && userPreferences.userType === UserType.Member,
			View: ProjectManagement
		},
		{
			id: dashboardTabId.groupAudit,
			label: labels.groupAudit,
			hash: '#groupAudit',
			visible: !!permissions?.canAccessGroupAudit,
			View: GroupAudit
		},
		{
			id: dashboardTabId.insights,
			label: labels.insights,
			hash: '#insights',
			visible: !!permissions?.canAccessAuditInsight && userPreferences.userType === UserType.Member,
			View: AuditInsight
		},
		{
			id: dashboardTabId.reporting,
			label: labels.reporting,
			hash: '#reporting',
			visible: !!permissions?.canAccessReporting,
			View: Reporting
		},
		{
			id: dashboardTabId.canvasClientPortal,
			label: labels.canvasClientPortal,
			hash: '#canvasClientPortal',
			visible: !!permissions?.canAccessClientPortal,
			View: CCP
		}
	];

	const userHasNoAccessToAnyTab = (permissions) => {
		return (
			permissions?.canAccessMilestone === false &&
			permissions?.canAccessProjectManagement === false &&
			permissions?.canAccessGroupAudit === false &&
			permissions?.canAccessAuditInsight === false &&
			permissions?.canAccessReporting === false &&
			permissions?.canAccessClientPortal === false
		);
	};

	useEffect(() => {
		// D1215536: [UAT3 Hercules]: While opening oversight project from Hercules Integration tab, page is getting blank.
		// The user does not have permission to access any tab of the dashboard.
		if (permissions && userHasNoAccessToAnyTab(permissions)) {
			// An API call is required to retrieve the RBAC error and prevent displaying a blank page.
			handleGetMetrics(1);
		}
		if (!permissions) handleGetPermissions();
		else if (!activeTab) handleHashAtLoad();
		else handleGetMetrics(activeTab?.id);
	}, [permissions, selectedEngagements.length, activeTab]);

	const handleGetPermissions = () => dispatch(getPermissions(projectId));

	const handleHashAtLoad = () => {
		const hash = location.hash;
		const visibleTabs = tabs.filter(({visible}) => visible);
		if (!!hash) {
			const hashTab = visibleTabs.find(({hash: hashTag}) => hashTag.toLowerCase() === hash.toLowerCase());
			setActiveTab(hashTab ? hashTab : visibleTabs[0]);
		} else {
			handleSetTab(visibleTabs[0]);
		}
	};

	const handleGetMetrics = (metricId) => {
		if (!isLoading) setIsLoading(true);
		dispatch(getEngagementMetrics(projectId, metricId, userPreferences?.userType, selectedEngagements)).finally(() => {
			setIsLoading(false);
		});
	};

	const handleSetTab = (newTab) => {
		history.replace(`${location.search}${newTab?.hash}`);
		setActiveTab(newTab);
	};

	const renderLastRefreshed = () => {
		const props = {
			className: 'last-refreshed',
			label: labels.lastRefreshed,
			date: lastRefreshed,
			body2: true,
			light: true,
			color: 'var(--neutrals-700)'
		};
		return <LabelAndDate {...props} />;
	};

	const renderInstructional = () => (
		<OutputText light>{labels.clickOnAVisualBelowToFilterTheEngagementList}</OutputText>
	);

	const renderTabs = () => {
		const props = {
			tabs,
			activeTab,
			setTab: handleSetTab,
			size: 'md',
			customSideContent: renderLastRefreshed
		};

		return <TabNavigation {...props} />;
	};

	const renderSidebar = () => <Sidebar moduleLabel={labels.dashboard} />;

	const renderLoader = () => <RightDashboardLoader />;

	const renderContent = () => {
		const props = {engagementMetrics: selectedMetrics};
		if (activeTab && activeTab.id === dashboardTabId.insights) props.isLoading = isLoading;
		return (
			<StyledDashboard>
				{renderTabs()}
				{renderInstructional()}
				<StyledDashboardGrid>{selectedMetrics && <activeTab.View {...props} />}</StyledDashboardGrid>
			</StyledDashboard>
		);
	};

	return (
		<TemplateWithSidebar renderSidebar={renderSidebar}>
			{isLoading || !orgChart ? renderLoader() : renderContent()}
		</TemplateWithSidebar>
	);
};

export default Dashboard;
